import CoverCardMVP from "../../components/shared/CoverCardMVP";
import {
	useLoaderData,
	useNavigate,
	useParams,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { Button } from "../../components/ui/button";
import "./../../styles/embeddedHtmlStyles.scss";
import { useReducer } from "react";
import { Skeleton } from "../../components/ui/skeleton";
import type { StudyStatus } from "../../routes/(app)/_app.study.$studyId";
import { AuthorsSimplyList } from "../../components/shared/AuthorsSimplyList";
import { WorkshopModalNoAuth } from "./WorkshopModalNoAuth";
import { useAuth } from "@/context/AuthContext";
import CoverCardNewVisual from "../../components/shared/CoverCardNewVisual";
import { FEATURE_FLAG_COVER_CARD_NEW_VISUAL } from "@/pages/Home";
import { ExternalHtml } from "@/components/ExternalHtml";

// First, define the types
export type DrawerState = {
	open: boolean;
	step: "go_to_login" | "go_to_website";
};

export type DrawerAction =
	| { type: "OPEN_DRAWER" }
	| { type: "CLOSE_DRAWER" }
	| { type: "SET_STEP"; payload: "go_to_login" | "go_to_website" };

// Define the initial state
const initialState: DrawerState = {
	open: false,
	step: "go_to_login",
};

const drawerReducer = (
	state: DrawerState,
	action: DrawerAction
): DrawerState => {
	switch (action.type) {
		case "OPEN_DRAWER":
			return {
				open: true,
				step: "go_to_login",
			};
		case "CLOSE_DRAWER":
			return {
				...state,
				open: false,
			};
		case "SET_STEP":
			return {
				...state,
				step: action.payload,
			};
		default:
			return state;
	}
};

export const WorkshopContentNoAuth = (): JSX.Element => {
	const { type } = useParams({
		from: "/(app)/_app/workshops_/$type/$workshopId",
	});
	const router = useRouter();
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();

	const { data } = useLoaderData({
		from: "/(app)/_app/workshops_/$type/$workshopId",
	});

	const [state, dispatch] = useReducer(drawerReducer, initialState);
	const search = useSearch({
		from: "/(app)/_app/workshops_/$type/$workshopId",
	});

	const workshopAction = async (): Promise<void> => {
		if (isAuthenticated) {
			await navigate({
				to: "/study/$studyId",
				params: { studyId: data?.slug ?? "" },
				search: { status: (data?.type_field as StudyStatus) ?? "Kurs-online" },
			});
		} else {
			dispatch({ type: "OPEN_DRAWER" });
		}
	};

	// const openAcademiaWebsite = async (): Promise<void> => {
	// 	await Browser.open({ url: "https://akademialtca.pl/" });
	// };

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			{data === undefined && (
				<>
					<Skeleton className="h-[40px] mb-4" />
					<Skeleton className="h-[175px] mb-4" />
					<Skeleton className="h-[36px] mb-4" />
					<Skeleton className="h-[48px] mb-4" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
				</>
			)}
			{data && (
				<>
					<div className="flex text-fontDarkGray pb-4">
						<button
							onClick={() => {
								router.history.back();
							}}
						>
							{data.type_field}
						</button>
						<span className="px-4">&#8226;</span>
						<span className="line-clamp-1">{data?.nazwa}</span>
					</div>
					{FEATURE_FLAG_COVER_CARD_NEW_VISUAL ? (
						<CoverCardNewVisual
							key={`CoverCardNewVisual-workshop ${data?.ID}`}
							authors={data.authors_users_data}
							categories={[data.tags]}
							coverId={Number(data.ID)}
							title={data.nazwa}
							coverDate={
								data.starts_at === "NULL" ||
								data.starts_at === undefined ||
								data.starts_at === ""
									? data["legal-status"]
									: data.starts_at
							}
						/>
					) : (
						<CoverCardMVP
							key={`CoverCardMVP-workshop ${data?.ID}`}
							authors={data.authors_users_data}
							categories={[data.tags]}
							coverId={Number(data.ID)}
							title={data.nazwa}
							coverDate={
								data.starts_at === "NULL" ||
								data.starts_at === undefined ||
								data.starts_at === ""
									? data["legal-status"]
									: data.starts_at
							}
						/>
					)}
					<WorkshopModalNoAuth dispatch={dispatch} drawerState={state} workshopType={type} />
					{/* <Drawer
						open={state.open}
						onOpenChange={(open) => {
							dispatch({ type: open ? "OPEN_DRAWER" : "CLOSE_DRAWER" });
						}}
					>
						<DrawerContentWithoutCancelButton>
							<div className="m-10 text-center">
								{state.step === "go_to_login" && (
									<>
										<img
											alt="website ilustration"
											className="mx-auto w-[130px] h-auto"
											src={websiteIllustration}
										/>
										<h2 className="text-xl text-[#637381] pb-4 text-center my-6">
											Czy posiadasz aktywny abonament LTCA lub masz wykupiony
											dostęp do {type === "webinar" ? "webinaru" : "webinaru"}?
										</h2>
										<div className="flex justify-around">
											<Button
												className="w-[150px] h-[50px] bg-[#919EAB14] text-black"
												// variant="secondary"
												onClick={() => {
													dispatch({
														type: "SET_STEP",
														payload: "go_to_website",
													});
												}}
											>
												NIE
											</Button>
											<Link to="/login">
												<Button className="w-[150px] h-[50px]">TAK</Button>
											</Link>
										</div>
									</>
								)}
								{state.step === "go_to_website" && (
									<>
										<h3 className="text-lg font-bold text-center mb-10">
											Wybierz sposób dostępu do kursu
										</h3>
										<p className="text-[#637381] mb-10">
											Możesz kupić dostęp do tego kursu lub wykupić abonament,
											który daje Ci dostęp do wszystkich wydarzeń na platformie.
										</p>
										<p className="text-[#637381] text-center mb-2">
											Dostęp do wszystkich wydarzeń na platformie
										</p>
										<Button
											className="w-full max-w-[600px]"
											onClick={async () => {
												await openWorkshopSite(
													"https://akademialtca.pl/abonament/"
												);
											}}
										>
											Wybierz Abonament
										</Button>
										<p className="text-[#637381] text-center mt-6 mb-2">
											Dostęp do wydarzenia na zawsze
										</p>
										<Button
											className="w-full max-w-[600px] bg-[#919EAB14] text-black"
											onClick={async () => {
												await openWorkshopSite(`https://akademialtca.pl/${studyWorkshopDetails.course.slug}`);
											}}
										>
											Kup dostęp
										</Button>
									</>
								)}
							</div>
						</DrawerContentWithoutCancelButton>
					</Drawer> */}
					{data?.original_url && data.type && (
						<Button
							className="inline-block w-full  my-4"
							onClick={workshopAction}
						>
							{search.status.toLowerCase().includes("online")
								? "Zapisz się"
								: "Rozpocznij"}
						</Button>
					)}
					<h1 className="text-2xl font-bold pb-4 pr-7">{data?.title}</h1>
					{data?.content && (
						<ExternalHtml
							html={data?.content}
							className="html-content text pr-7"
						/>
					)}
					<h5 className="text-sm font-medium pb-3">
						{data.authors_users_data.length >= 2
							? "Wykładowcy:"
							: "Wykładowca:"}
					</h5>
					<AuthorsSimplyList authors={data.authors_users_data} />
				</>
			)}
		</main>
	);
};
